import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faApple,
  faFacebookMessenger,
  faInstagram,
  faLine,
  faViber,
  faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import {
  faCheckSquare,
  faCheckToSlot,
  faEnvelope,
  faEye,
  faMessage,
  faMessageLines,
  faMessageSmile,
  faPaperPlane,
  faPhone,
  faPhoneArrowDown,
  faPhoneArrowUp,
  faPhoneArrowUpRight,
  faPhoneHangup,
  faPhoneMissed,
  faPhoneXmark,
  faQuestionCircle,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import _startCase from 'lodash.startcase';

import { ChatPlatform, ConstValues, TChatPlatform } from '@cloud-wave/neon-common-lib';

import { ValueOf } from 'lib/core/types';

import CONNECTION_TYPES from 'lib/common/constants/connectionType';
import CONTACT_STATES from 'lib/common/constants/contactStates';
import CONTACT_TYPES from 'lib/common/constants/contactTypes';

import faCalendarPhone from 'lib/common/customIcons/faCalendarPhone';
import TChatTask from 'lib/common/types/ChatTask';
import TTask from 'lib/common/types/Task';

export const TASK_COLOURS = {
  [CONTACT_STATES.MISSED]: 'danger',
  [CONTACT_STATES.REJECTED]: 'danger',
  [CONTACT_STATES.CONNECTING]: 'darkBlue',
  [CONTACT_STATES.CONNECTED]: 'darkBlue',
  [CONTACT_STATES.ACW]: 'darkBlue',
  [CONTACT_STATES.DRAFT]: 'darkBlue',
  [CONTACT_STATES.PAUSED]: 'darkBlue',
};

type TChatTypeProps = Record<TChatPlatform, { icon: IconDefinition; subtitle: string }>;

const SUB_TITLE_TEMPLATES = (TYPE: keyof typeof CONTACT_TYPES) => {
  const type = _startCase(TYPE.toLowerCase());
  return {
    [CONNECTION_TYPES.INBOUND]: `Incoming ${type}`,
    [CONNECTION_TYPES.MONITORING]: `Supervised ${type}`,
    [CONNECTION_TYPES.OUTBOUND]: `Outgoing ${type}`,
    [CONTACT_STATES.MISSED]: `Missed ${type}`,
    [CONTACT_STATES.ACCEPTED]: `Incoming ${type}`,
    [CONTACT_STATES.PENDING]: `Pending ${type}`,
    [CONTACT_STATES.ERROR]: `Error ${type}`,
    [CONTACT_STATES.ACW]: `After ${type} Work`,
    [CONTACT_STATES.CONNECTED]: `Connected ${type}`,
    [CONTACT_STATES.REJECTED]: `Rejected ${type}`,
    [CONTACT_STATES.DRAFT]: 'Draft',
    [CONTACT_STATES.PAUSED]: `Paused ${type}`,
  };
};

type chatSubIcons = Record<string, IconDefinition>;

export const CHAT_SUB_TYPES_ICONS: chatSubIcons = {
  [ChatPlatform.DEFAULT]: faMessage,
  [ChatPlatform.MESSENGER]: faFacebookMessenger,
  [ChatPlatform.WHATSAPP]: faWhatsapp,
  [ChatPlatform.INSTAGRAM]: faInstagram,
  [ChatPlatform.VIBER]: faViber,
  [ChatPlatform.VIBERBM]: faViber,
  [ChatPlatform.TELEGRAM]: faPaperPlane,
  [ChatPlatform.LINE]: faLine,
  [ChatPlatform.RCS]: faMessageLines,
  [ChatPlatform.SMS]: faMessageLines,
  [ChatPlatform.MMS]: faMessageLines,
  [ChatPlatform.APPLE]: faApple,
  FALLBACK: faMessageSmile,
} as const;

export const CHAT_SUB_TYPES_TOOLTIPS = {
  [ChatPlatform.DEFAULT]: 'Web Chat',
  [ChatPlatform.MESSENGER]: 'Messenger',
  [ChatPlatform.WHATSAPP]: 'WhatsApp',
  [ChatPlatform.INSTAGRAM]: 'Instagram',
  [ChatPlatform.VIBER]: 'Viber',
  [ChatPlatform.VIBERBM]: 'Viber',
  [ChatPlatform.TELEGRAM]: 'Telegram',
  [ChatPlatform.LINE]: 'LINE',
  [ChatPlatform.RCS]: 'RCS',
  [ChatPlatform.SMS]: 'SMS',
  [ChatPlatform.MMS]: 'MMS',
  [ChatPlatform.APPLE]: 'Apple',
  FALLBACK: 'External Chat',
} as const;

export const chatPlatformHasCustomIcon = (neonChatPlatform) =>
  Object.keys(CHAT_SUB_TYPES_ICONS).includes(neonChatPlatform);

export const getChatProps = ({ neonChatPlatform, status, type, connectionType }: TChatTask, props) => {
  const chatPlatform = chatPlatformHasCustomIcon(neonChatPlatform) ? neonChatPlatform : 'FALLBACK';

  if (connectionType === CONNECTION_TYPES.MONITORING && status === CONTACT_STATES.CONNECTED) {
    return {
      icon: faEye,
      subtitle: 'Supervised Chat',
      tooltip: 'Supervised Chat',
    };
  }

  return {
    ...(props?.[status]?.[type]?.[chatPlatform] ?? {}),
    tooltip: CHAT_SUB_TYPES_TOOLTIPS[neonChatPlatform] || CHAT_SUB_TYPES_TOOLTIPS.FALLBACK,
  };
};

export const getTaskDisplayProps = (task?: TChatTask | TTask) => {
  if (!task) {
    return { icon: faQuestionCircle, subtitle: '' };
  }
  const isChat = task.type === CONTACT_TYPES.CHAT;
  const allTaskProps = TASK_TYPES(task.connectionType);

  return isChat
    ? getChatProps(task as TChatTask, allTaskProps)
    : (allTaskProps?.[task.status]?.[task.type] ?? { icon: faQuestionCircle, subtitle: 'Unknown Task Type' });
};

const getChatSubTypePropsForUseCase = (
  state: keyof typeof CONTACT_STATES | ConstValues<typeof CONNECTION_TYPES>
): TChatTypeProps => {
  const subtitles = SUB_TITLE_TEMPLATES('CHAT');
  const entriesNeedingIconSubtitle = Object.entries(CHAT_SUB_TYPES_ICONS) as [TChatPlatform, IconDefinition][];

  return entriesNeedingIconSubtitle.reduce<TChatTypeProps>((accumulator, [key, value]) => {
    return { ...accumulator, [key]: { icon: value, subtitle: subtitles[state] } };
  }, {} as TChatTypeProps);
};

const DEFAULT_INCOMING_TASK_PROPS = {
  [CONNECTION_TYPES.INBOUND]: {
    [CONTACT_TYPES.CALL]: { icon: faPhoneArrowDown, subtitle: `Incoming Call` },
    [CONTACT_TYPES.QUEUE_CALLBACK]: {
      icon: faCalendarPhone,
      subtitle: 'Incoming Callback',
    },
    [CONTACT_TYPES.CHAT]: getChatSubTypePropsForUseCase(CONNECTION_TYPES.INBOUND),
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: `Incoming Email` },
    [CONTACT_TYPES.TASK]: { icon: faCheckSquare, subtitle: `Incoming Agent Task` },
    [CONTACT_TYPES.OUTBOUND_PREVIEW]: { icon: faPhoneArrowUp, subtitle: `Incoming Outbound Preview` },
    [CONTACT_TYPES.PREVIEW_DIALING]: { icon: faPhoneArrowUp, subtitle: `Incoming Outbound Preview` },
    [CONTACT_TYPES.CONFERENCE_CALL]: { icon: faUsers, subtitle: 'Connecting Call' },
  },
  [CONNECTION_TYPES.AGENT]: {
    [CONTACT_TYPES.CALL]: { icon: faPhoneArrowDown, subtitle: `Incoming Call` },
    [CONTACT_TYPES.CHAT]: getChatSubTypePropsForUseCase(CONNECTION_TYPES.AGENT),
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: `Incoming Email` },
    [CONTACT_TYPES.OUTBOUND_PREVIEW]: { icon: faPhoneArrowUp, subtitle: `Incoming Outbound Preview` },
    [CONTACT_TYPES.PREVIEW_DIALING]: { icon: faPhoneArrowUp, subtitle: `Incoming Outbound Preview` },
    [CONTACT_TYPES.TASK]: { icon: faCheckSquare, subtitle: `Incoming Agent Task` },
  },
  [CONNECTION_TYPES.MONITORING]: {
    [CONTACT_TYPES.CALL]: { icon: faEye, subtitle: `Supervised Call` },
    [CONTACT_TYPES.CHAT]: getChatSubTypePropsForUseCase(CONNECTION_TYPES.MONITORING),
    [CONTACT_TYPES.CONFERENCE_CALL]: { icon: faEye, subtitle: `Supervised Call` },
  },
  [CONNECTION_TYPES.OUTBOUND]: {
    [CONTACT_TYPES.CALL]: {
      icon: faPhoneArrowUpRight,
      subtitle: 'Outbound Call',
    },
    [CONTACT_TYPES.CONFERENCE_CALL]: { icon: faUsers, subtitle: 'Connecting Call' },
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: 'Email' },
  },
};

const DEFAULT_ACCEPTED_TASK_PROPS = {
  ...DEFAULT_INCOMING_TASK_PROPS,
  [CONNECTION_TYPES.INBOUND]: {
    ...DEFAULT_INCOMING_TASK_PROPS[CONNECTION_TYPES.INBOUND],
    [CONTACT_TYPES.QUEUE_CALLBACK]: {
      icon: faCalendarPhone,
      subtitle: 'Connecting Callback',
    },
  },
  [CONNECTION_TYPES.AGENT]: {
    ...DEFAULT_INCOMING_TASK_PROPS[CONNECTION_TYPES.AGENT],
    [CONTACT_TYPES.PREVIEW_DIALING]: {
      icon: faPhoneArrowUp,
      subtitle: 'Outbound Preview',
    },
  },
  [CONNECTION_TYPES.OUTBOUND]: {
    ...DEFAULT_INCOMING_TASK_PROPS[CONNECTION_TYPES.OUTBOUND],
    [CONTACT_TYPES.CALL]: {
      icon: faPhone,
      subtitle: 'Connecting Call',
    },
  },
};

const getConnectedTaskPropsForConnectionType = (connectionType: ValueOf<typeof CONNECTION_TYPES>) => {
  if (connectionType === CONNECTION_TYPES.MONITORING) {
    return {
      [CONTACT_TYPES.CALL]: { icon: faEye, subtitle: 'Supervised Call' },
      [CONTACT_TYPES.QUEUE_CALLBACK]: { icon: faEye, subtitle: 'Supervised Call' },
      [CONTACT_TYPES.CHAT]: { icon: faEye, subtitle: 'Supervised Chat' },
      [CONTACT_TYPES.CONFERENCE_CALL]: { icon: faEye, subtitle: 'Supervised Call' },
    };
  }

  return {
    [CONTACT_TYPES.CALL]: { icon: faPhone, subtitle: 'Connected Call' },
    [CONTACT_TYPES.QUEUE_CALLBACK]: { icon: faPhone, subtitle: 'Connected Call' },
    [CONTACT_TYPES.CONFERENCE_CALL]: { icon: faPhone, subtitle: 'Connected Call' },
    [CONTACT_TYPES.CHAT]: getChatSubTypePropsForUseCase(CONTACT_STATES.CONNECTED),
    [CONTACT_TYPES.TASK]: { icon: faCheckSquare, subtitle: 'Active Agent Task' },
    [CONTACT_TYPES.OUTBOUND_PREVIEW]: { icon: faPhoneArrowUp, subtitle: 'Outbound Preview' },
    [CONTACT_TYPES.PREVIEW_DIALING]: { icon: faPhoneArrowUp, subtitle: 'Outbound Preview' },
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: 'Active Email' },
  };
};

export const TASK_TYPES = (connectionType: ValueOf<typeof CONNECTION_TYPES>) => ({
  [CONTACT_STATES.MISSED]: {
    [CONTACT_TYPES.CALL]: { icon: faPhoneMissed, subtitle: 'Missed Call' },
    [CONTACT_TYPES.QUEUE_CALLBACK]: { icon: faPhoneMissed, subtitle: 'Missed Callback' },
    [CONTACT_TYPES.CHAT]: getChatSubTypePropsForUseCase(CONTACT_STATES.MISSED),
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: 'Missed Email' },
    [CONTACT_TYPES.TASK]: { icon: faCheckSquare, subtitle: 'Missed Agent Task' },
    [CONTACT_TYPES.OUTBOUND_PREVIEW]: { icon: faPhoneArrowUp, subtitle: 'Missed Outbound Preview' },
    [CONTACT_TYPES.PREVIEW_DIALING]: { icon: faPhoneArrowUp, subtitle: 'Missed Outbound Preview' },
  },
  [CONTACT_STATES.CONNECTING]: DEFAULT_INCOMING_TASK_PROPS[connectionType],
  [CONTACT_STATES.PENDING]: DEFAULT_ACCEPTED_TASK_PROPS[connectionType],
  [CONTACT_STATES.ERROR]: {
    [CONTACT_TYPES.PREVIEW_DIALING]: { icon: faPhoneArrowUp, subtitle: 'Outbound Preview' },
  },
  [CONTACT_STATES.ACCEPTED]: DEFAULT_ACCEPTED_TASK_PROPS[connectionType],
  [CONTACT_STATES.ACW]: {
    [CONTACT_TYPES.CALL]: { icon: faPhoneHangup, subtitle: 'After Call Work' },
    [CONTACT_TYPES.QUEUE_CALLBACK]: { icon: faPhoneHangup, subtitle: 'After Call Work' },
    [CONTACT_TYPES.CHAT]: getChatSubTypePropsForUseCase(CONTACT_STATES.ACW),
    [CONTACT_TYPES.TASK]: { icon: faCheckToSlot, subtitle: 'After Task Work' },
    [CONTACT_TYPES.OUTBOUND_PREVIEW]: { icon: faPhoneArrowUp, subtitle: 'After Outbound Preview Work' },
    [CONTACT_TYPES.PREVIEW_DIALING]: { icon: faPhoneArrowUp, subtitle: 'After Outbound Preview Work' },
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: 'After Email Work' },
    [CONTACT_TYPES.CONFERENCE_CALL]: { icon: faPhone, subtitle: 'After Call Work' },
  },
  [CONTACT_STATES.CONNECTED]: getConnectedTaskPropsForConnectionType(connectionType),
  [CONTACT_STATES.REJECTED]: {
    [CONTACT_TYPES.CALL]: { icon: faPhoneXmark, subtitle: 'Rejected Call' },
    [CONTACT_TYPES.QUEUE_CALLBACK]: { icon: faPhoneXmark, subtitle: 'Rejected Callback' },
    [CONTACT_TYPES.CHAT]: getChatSubTypePropsForUseCase(CONTACT_STATES.REJECTED),
    [CONTACT_TYPES.TASK]: { icon: faCheckSquare, subtitle: 'Rejected Agent Task' },
    [CONTACT_TYPES.OUTBOUND_PREVIEW]: { icon: faPhoneArrowUp, subtitle: 'Rejected Outbound Preview' },
    [CONTACT_TYPES.PREVIEW_DIALING]: { icon: faPhoneArrowUp, subtitle: 'Rejected Outbound Preview' },
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: 'Rejected Email' },
    [CONTACT_TYPES.CONFERENCE_CALL]: { icon: faPhone, subtitle: 'Rejected Call' },
  },
  [CONTACT_STATES.DRAFT]: {
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: 'Draft' },
  },
  [CONTACT_STATES.PAUSED]: {
    [CONTACT_TYPES.TASK]: { icon: faCheckSquare, subtitle: 'Paused Agent Task' },
    [CONTACT_TYPES.EMAIL]: { icon: faEnvelope, subtitle: 'Paused Email' },
  },
});
